/**
 * ApexHuntSystem — Phase 1's escalating recurring-event hazard.
 *
 * Every 65–100s after a 90-120s grace window, a wave of phages
 * converges from one screen edge. The wave size + composition scale
 * with the cell's biomass:
 *
 *   25k:  4 lytic phages          ('Lytic Tide')
 *   80k:  6 lytic phages          ('Phage Storm')
 *   180k: 6 phages + 1 apex phage ('Apex Convergence')
 *
 * The first time each tier fires, a unique fanfare banner runs during
 * the 4s warning window so the player has time to read the threat
 * description and reposition. Phages enter at the predator system's
 * existing chase AI, so once on-screen they pivot toward the player —
 * the wave is about quantity + timing, not movement choreography.
 *
 * Sits parallel to the regular SpawnSystem (which keeps trickling its
 * normal predator pool) — the hunt is layered on top, never replacing
 * baseline pressure.
 */

import { Container } from 'pixi.js';
import { bus } from '@/core/EventBus';
import type { World } from '@/core/ECS';
import type { PredatorDef } from '@/data/types';
import { createPredator } from '@/entities/createPredator';
import { player } from '@/systems/PlayerState';

interface HuntTier {
  /** Biomass at which this tier becomes the active wave size. */
  biomass: number;
  /** Number of phages spawned in the wave. */
  count: number;
  /** Whether to spawn an apex_phage at the wave center. */
  withApex: boolean;
  /** First-fire fanfare banner. */
  unlockTitle: string;
  unlockSubtitle: string;
}

const TIERS: HuntTier[] = [
  { biomass: 25_000,  count: 4, withApex: false, unlockTitle: 'Lytic Tide',         unlockSubtitle: 'Phages converge from the dark — defend yourself' },
  { biomass: 80_000,  count: 6, withApex: false, unlockTitle: 'Phage Storm',        unlockSubtitle: 'A cascade of lytic phages closes in' },
  { biomass: 180_000, count: 6, withApex: true,  unlockTitle: 'Apex Convergence',   unlockSubtitle: 'An apex hunts at the head of the wave — survive' },
];

const COOLDOWN_MIN = 65;
const COOLDOWN_MAX = 100;
const WARNING_SEC = 4;
// Phages enter slightly faster than baseline so the wave reads as
// "incoming pressure", not the usual trickle.
const ENTRY_SPEED_BOOST = 1.15;

type Phase = 'idle' | 'warn';

export class ApexHuntSystem {
  private world: World;
  private parent: Container;
  private predatorDefs: Record<string, PredatorDef>;
  private cooldownT: number;
  private warnT = 0;
  private phase: Phase = 'idle';
  private firedTiers = new Set<number>();
  private pendingTier: HuntTier | null = null;
  private pendingSide = 0;

  constructor(world: World, parent: Container, predatorDefs: Record<string, PredatorDef>) {
    this.world = world;
    this.parent = parent;
    this.predatorDefs = predatorDefs;
    // Long initial grace so the player has room to find their feet
    // before the first tide arrives.
    this.cooldownT = 90 + Math.random() * 30;
  }

  reset(): void {
    this.phase = 'idle';
    this.warnT = 0;
    this.pendingTier = null;
    this.firedTiers.clear();
    this.cooldownT = 90 + Math.random() * 30;
  }

  update(dt: number, vw: number, vh: number): void {
    const tier = this.activeTier();
    if (!tier) return;

    if (this.phase === 'idle') {
      this.cooldownT -= dt;
      if (this.cooldownT <= 0) {
        this.pendingTier = tier;
        this.pendingSide = Math.floor(Math.random() * 4);
        this.phase = 'warn';
        this.warnT = WARNING_SEC;
        const tierIdx = TIERS.indexOf(tier);
        const isFirst = !this.firedTiers.has(tierIdx);
        this.firedTiers.add(tierIdx);
        bus.emit('stage:goalBanner', {
          title: isFirst ? tier.unlockTitle : `${tier.unlockTitle} — incoming`,
          subtitle: tier.unlockSubtitle,
          durationSec: WARNING_SEC,
        });
        bus.emit('audio:cue', { id: 'event' });
      }
      return;
    }

    // warn
    this.warnT -= dt;
    if (this.warnT <= 0 && this.pendingTier) {
      this.spawnWave(this.pendingTier, this.pendingSide, vw, vh);
      this.pendingTier = null;
      this.phase = 'idle';
      this.cooldownT = COOLDOWN_MIN + Math.random() * (COOLDOWN_MAX - COOLDOWN_MIN);
    }
  }

  private activeTier(): HuntTier | null {
    let best: HuntTier | null = null;
    for (const t of TIERS) {
      if (player.biomass >= t.biomass) best = t;
    }
    return best;
  }

  private spawnWave(tier: HuntTier, side: number, vw: number, vh: number): void {
    const phage = this.predatorDefs['phage_pierce'];
    const apex = this.predatorDefs['apex_phage'];
    if (!phage) return;
    const points = this.pointsAlongEdge(side, vw, vh, tier.count);
    for (const [x, y] of points) {
      createPredator(this.world, this.parent, phage, x, y, { speed: ENTRY_SPEED_BOOST });
    }
    if (tier.withApex && apex) {
      const [ax, ay] = this.pointsAlongEdge(side, vw, vh, 1)[0];
      createPredator(this.world, this.parent, apex, ax, ay, {});
    }
  }

  private pointsAlongEdge(side: number, vw: number, vh: number, count: number): Array<[number, number]> {
    const m = 60;
    const out: Array<[number, number]> = [];
    for (let i = 0; i < count; i++) {
      const t = count === 1 ? 0.5 : i / (count - 1);
      switch (side) {
        case 0: out.push([-m, t * vh]); break;
        case 1: out.push([vw + m, t * vh]); break;
        case 2: out.push([t * vw, -m]); break;
        case 3: out.push([t * vw, vh + m]); break;
      }
    }
    return out;
  }
}
